import { z } from "zod";
import { CanonicalUnitSchema, IsoDateSchema, IsoDateTimeSchema, LocaleSchema, QuantitySchema, } from "./primitives.js";
// GET /v1/pantry response
export const PantryItemStateSchema = z.enum([
    "available",
    "reserved",
    "consumed",
    "expired",
    "discarded",
]);
export const PantryItemSourceSchema = z.enum([
    "quick_add",
    "extraction",
    "leftover_proposal",
]);
export const PantryItemSchema = z.object({
    id: z.string(),
    canonicalIngredientId: z.string().nullable(),
    displayName: z.string(),
    quantity: QuantitySchema,
    uncertain: z.boolean(),
    uncertaintyRange: z
        .object({ min: z.number(), max: z.number() })
        .nullable(),
    storageLocation: z.string().nullable(),
    expiryDate: IsoDateSchema.nullable(),
    state: PantryItemStateSchema,
    source: PantryItemSourceSchema,
    confidence: z.number().min(0).max(1),
    revision: z.number().int().nonnegative(),
});
export const PantryResponseSchema = z.object({
    items: z.array(PantryItemSchema),
    revision: z.number().int().nonnegative(),
});
// POST /v1/pantry/extractions — non-mutating. Request/response shapes match
// data-model.md's "Pantry capture" example exactly.
export const PantryExtractionRequestSchema = z.object({
    sourceText: z.string().min(1),
    locale: LocaleSchema,
});
export const PantryCandidateSchema = z.object({
    candidateId: z.string(),
    displayName: z.string(),
    canonicalIngredientId: z.string().nullable(),
    /** WS6: a negative extracted quantity is rejected at the contract, like every other pantry write. */
    quantity: z.number().nonnegative(),
    unit: z.string(),
    /** WS3 (audit R-09): never negative — stock is added, never subtracted, through a confirmation. */
    normalizedQuantity: z.number().nonnegative(),
    normalizedUnit: CanonicalUnitSchema,
    confidence: z.number().min(0).max(1),
    needsConfirmation: z.boolean(),
    /** "Did you mean" offers for an unmatched guess (owner 2026-08-30:
     * typing a category like "Fish" dead-ended) — the user taps to choose,
     * never auto-picked. Default [] keeps old servers/clients compatible. */
    suggestions: z
        .array(z.object({ ingredientId: z.string(), nameDe: z.string(), nameEn: z.string() }))
        .default([]),
});
export const PantryExtractionResponseSchema = z.object({
    sourceText: z.string(),
    locale: z.string(),
    candidates: z.array(PantryCandidateSchema),
    /** Bound to the candidate payload hash; required by /pantry/confirmations. */
    confirmationToken: z.string(),
    expiresAt: IsoDateTimeSchema,
});
// POST /v1/pantry/confirmations request
export const PantryConfirmedCandidateSchema = z.object({
    candidateId: z.string(),
    include: z.boolean(),
    canonicalIngredientId: z.string().nullable(),
    normalizedQuantity: z.number(),
    normalizedUnit: CanonicalUnitSchema,
    storageLocation: z.string().nullable(),
    expiryDate: IsoDateSchema.nullable(),
});
export const PantryConfirmationRequestSchema = z.object({
    confirmationToken: z.string(),
    candidates: z.array(PantryConfirmedCandidateSchema).min(1),
});
// PATCH /v1/pantry/items/{id} request — direct, user-authored inventory edit
// (Pantry page). Unlike extraction, nothing here is model-proposed: the user
// types the exact value and the explicit save IS the approval step, so no
// confirmation token is involved (the token pattern binds LLM-extracted
// candidates only). Quantity is in the item's own canonical unit; setting it
// clears `uncertain` because a human just asserted a definite amount.
export const PantryItemUpdateRequestSchema = z.object({
    quantity: z.number().nonnegative(),
    expiryDate: IsoDateSchema.nullable().optional(),
});
//# sourceMappingURL=pantry.js.map